import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import { ColumnsType } from 'antd/lib/table/interface';
import {
	faInbox,
	faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { Moment } from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import {
	Checkbox,
	Space,
	Tag,
} from 'antd';
import {
	Currencies,
	DATE,
	EstimateStatus,
	EstimateStatusLabels,
	FixtureTypeLabels,
	FixtureTypes,
} from '@shared/utils/constants';
import {
	formatDate,
	toMoment,
} from '@shared/utils/date';
import { Values } from '@shared/utils/objectEnums';
import { standardSort } from '@shared/utils/standardSort';
import { round } from '@shared/utils/math';
import { formatCurrency } from '@shared/utils/currency';
import { capitalize } from '@shared/utils/string';
import { GetEstimateGroupsResponse } from '@api/features/estimates/getEstimateGroups';
import {
	createEstimate,
	getEstimateGroups,
	copyEstimate,
	deleteEstimateGroup,
} from '@client/lib/api';
import { renderDate } from '@client/utils/table';
import ListTableScreen from '@client/components/screens/ListTableScreen';
import { Links } from '@client/utils/links';
import { FormPopoverProps } from '@client/components/FormPopover';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import VesselDescription from '@client/components/VesselDescription';
import VesselSelector from '@client/components/VesselSelector';
import { getEstimateStateColor } from '@client/utils/colors';
import showErrorNotification from '@client/utils/showErrorNotification';
import Button from '@client/components/Button';
import { useTableQuery } from '@client/utils/hooks/useTableQuery';
import { stringSetToSortedList } from '@client/utils/stringSetToSortedList';

type EstimateOverviewTableRow = {
	id: string;
	estimateGroupId: number;
	firstEstimateId: number;
	name: string | null;
	vesselName: string | null;
	vesselFlag: string | null;
	createdAt: Moment;
	tce: number | null;
	currency: Values<typeof Currencies>;
	firstLoadPortName: string | null;
	lastDischargePortName: string | null;
	commenceDate: Moment | null;
	charterers: string | null;
	earliestLaycanFrom: Moment | null;
	earliestLaycanTo: Moment | null;
	fixedBy: string | null;
	operatedBy: string | null;
	hasFixedEstimate: boolean;
	status: EstimateStatus;
	type: Values<typeof FixtureTypes>;
};

type CreateEstimateFormData = {
	type: Values<typeof FixtureTypes>;
	vesselId?: number;
	name?: string;
	charterer?: number;
	currency: Values<typeof Currencies>;
};

const EstimatesListPage: React.FC = () => {
	const [hideFixed, setHideFixed] = useState(true);

	const {
		data: estimateGroups,
		isLoading,
		error,
		refetch,
		pagination,
		setSearch,
		onTableChange,
	} = useTableQuery<GetEstimateGroupsResponse>({
		queryKey: ['estimateGroups', hideFixed],
		fetcher: ({ pagination, filters, sorter, search }) =>
			getEstimateGroups({
				hideFixed,
				pagination,
				filters,
				sorter,
				search
			}),
		defaultPageSize: 100,
	});

	const onDeleteEstimateGroup = useCallback(async (id: number) => {
		try {
			await deleteEstimateGroup(id);
			refetch();
			showSuccessNotification('Estimate group deleted');
		} catch (e) {
			showErrorNotification('Could not delete estimate group', e as Error);
		}
	}, [refetch]);

	const filterMaps = useMemo(() => {
		const estimateNames = new Set<string>();
		const loadPorts = new Set<string>();
		const disPorts = new Set<string>();
		const charterers = new Set<string>();
		const vesselsInEstimate = new Set<string>();
		const fixedByList = new Set<string>();
		const operatedByList = new Set<string>();
		const estimateTypes = new Set<Values<typeof FixtureTypes>>();

		estimateGroups?.rows.forEach((item) => {
			if (item.groupName != null) {
				estimateNames.add(item.groupName);
			}

			if (item.firstLoadPortName != null) {
				loadPorts.add(item.firstLoadPortName);
			}

			if (item.lastDischargePortName != null) {
				disPorts.add(item.lastDischargePortName);
			}

			if (item.charterers != null) {
				charterers.add(item.charterers);
			}

			if (item.vesselName != null) {
				vesselsInEstimate.add(item.vesselName);
			}

			if (item.fixedBy != null) {
				fixedByList.add(item.fixedBy);
			}

			if (item.operatedBy != null) {
				operatedByList.add(item.operatedBy);
			}

			if (item.type != null) {
				estimateTypes.add(item.type);
			} 
		});

		return {
			estimateNames: stringSetToSortedList<string>(estimateNames),
			loadPorts: stringSetToSortedList<string>(loadPorts),
			disPorts: stringSetToSortedList<string>(disPorts),
			charterers: stringSetToSortedList<string>(charterers),
			vesselsInEstimate: stringSetToSortedList<string>(vesselsInEstimate),
			fixedByList: stringSetToSortedList<string>(fixedByList),
			operatedByList: stringSetToSortedList<string>(operatedByList),
			estimateTypes: stringSetToSortedList<Values<typeof FixtureTypes>>(estimateTypes),
		};
	}, [estimateGroups]);

	const estimateRows: EstimateOverviewTableRow[] = useMemo(() => (
		(estimateGroups?.rows ?? [])
			.slice()
			.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
			.map((e) => ({
				id: e.readableId,
				firstEstimateId: e.firstEstimateId,
				estimateGroupId: e.id,
				name: e.groupName,
				vesselName: e.vesselName ?? null,
				vesselFlag: e.vesselFlag,
				createdAt: toMoment(e.createdAt),
				tce: e?.tce ?? null,
				currency: e.currency ?? null,
				status: e.status ?? EstimateStatus.Pending,
				firstLoadPortName: e.firstLoadPortName ?? null,
				lastDischargePortName: e.lastDischargePortName ?? null,
				commenceDate: e.commenceDate ?? null,
				completeDate: e.completeDate ?? null,
				charterers: e.charterers ?? null,
				brokers: e.brokers ?? null,
				earliestLaycanFrom: e.earliestLaycanFrom,
				earliestLaycanTo: e.earliestLaycanTo,
				fixedBy: e.fixedBy,
				operatedBy: e.operatedBy,
				hasFixedEstimate: e.hasFixedEstimate,
				type: e.type,
			}))
	), [estimateGroups]);

	const columns: ColumnsType<EstimateOverviewTableRow> = useMemo(() => [
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 60,
			render: (state: EstimateStatus) => (
				<Tag color={getEstimateStateColor(state)}>
					{capitalize(
						EstimateStatusLabels[
							(state || EstimateStatus.Pending) as keyof typeof EstimateStatusLabels
						],
					)}
				</Tag>
			),
			filters: [{
				text: EstimateStatusLabels[EstimateStatus.Pending],
				value: EstimateStatus.Pending,
			}, {
				text: EstimateStatusLabels[EstimateStatus.Fixed],
				value: EstimateStatus.Fixed,
			}],
			onFilter: (value, record) => record.status === value,
		},
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 85,
		},
		{
			title: 'Type',
			dataIndex: 'type',
			width: 50,
			render: (t: Values<typeof FixtureTypes>) => FixtureTypeLabels[t],
			key: 'type',
			filterMultiple: true,
			ellipsis: true,
			filters: filterMaps.estimateTypes.map((type) => ({
				text: FixtureTypeLabels[type],
				value: type,
			})),
			onFilter: (type, record) => record.type === type,
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			filterMultiple: true,
			ellipsis: true,
			width: 150,
		},
		{
			title: 'Vessel',
			dataIndex: 'vesselName',
			key: 'vesselName',
			sorter: standardSort('vesselName'),
			filterMultiple: true,
			ellipsis: true,
			render: (_, est) => (
				<VesselDescription
					name={est.vesselName ?? '—'}
					flag={est.vesselFlag}
				/>
			),
			width: 120,
		},
		{
			title: '1st Loadport',
			dataIndex: 'firstLoadPortName',
			key: 'firstLoadPortName',
			filterMultiple: true,
			ellipsis: true,
			width: 110,
		},
		{
			title: 'Last Disport',
			dataIndex: 'lastDischargePortName',
			key: 'lastDischargePortName',
			ellipsis: true,
			width: 110,
		},
		{
			title: '1st Laydays',
			dataIndex: 'earliestLaycanFrom',
			key: 'earliestLaycanFrom',
			render: (date: Moment) => (date == null ? '-' : formatDate(date, DATE, false)),
			sorter: standardSort('earliestLaycanFrom'),
			width: 70,
		},
		{
			title: '1st Cancelling',
			dataIndex: 'earliestLaycanTo',
			key: 'earliestLaycanTo',
			render: (date: Moment) => (date == null ? '-' : formatDate(date, DATE, false)),
			sorter: standardSort('earliestLaycanTo'),
			width: 80,
		},
		{
			title: 'Charterers',
			dataIndex: 'charterers',
			key: 'charterers',
			filterMultiple: true,
			onFilter: (value, record) => record.charterers === value,
			render: (charterers) => {
				if (charterers == null) {
					return '-';
				}

				;

				const chartererList = charterers.split(',');

				if (chartererList?.length <= 5) {
					return charterers;
				}

				return `${chartererList?.slice(0, 5).join(', ')} and ${chartererList?.length - 5} more`;
			},
			width: 110,
		},
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			sorter: standardSort('createdAt'),
			defaultSortOrder: 'descend',
			render: renderDate(DATE),
			width: 80,
		},
		{
			title: 'Fixed By',
			dataIndex: 'fixedBy',
			key: 'fixedBy',
			filterMultiple: true,
			ellipsis: true,
			onFilter: (value, record) => record.fixedBy === value,
			sorter: (a, b) => (a.fixedBy ?? '').localeCompare(b.fixedBy ?? ''),
			width: 100,
		},
		{
			title: 'Operated By',
			dataIndex: 'operatedBy',
			key: 'operatedBy',
			filterMultiple: true,
			ellipsis: true,
			onFilter: (value, record) => record.operatedBy === value,
			sorter: (a, b) => (a.operatedBy ?? '').localeCompare(b.operatedBy ?? ''),
			width: 110,
		},
		{
			title: 'TCE / day',
			dataIndex: 'tce',
			key: 'tce',
			sorter: standardSort('tce'),
			render: (value, row) => {
				if (value == null) {
					return '—';
				}

				const roundedValue = round(value, 0);

				if (row.currency == null) {
					return roundedValue;
				}

				return formatCurrency(roundedValue, row.currency);
			},
			width: 80,
		},
		{
			title: '',
			align: 'right',
			render: (_, row) => (
				<Space data-no-row-click="true">
					<Button
						type="link"
						onClick={async (e: any) => {
							e.stopPropagation();
							await copyEstimate(row.firstEstimateId);
							showSuccessNotification(
								'Estimate copied',
								`The estimate ${row.name} was successfully copied`,
								4,
							);
							await refetch();
						}}
						icon={(
							<FontAwesomeIcon
								icon={faCopy as IconProp}
							/>
						)}
					/>
					<Button
						type="link"
						disabled={row.hasFixedEstimate}
						disabledTooltip="Group has one or more fixed estimates"
						icon={(
							<FontAwesomeIcon
								icon={faTrash as IconProp}
							/>
						)}
						danger
						confirmTitle={`
							All estimates in this group will be deleted. 
							Cargos will not be deleted, but will be marked as available. Are you sure you wish to continue?
						`}
						onClick={async (e: any) => {
							e.stopPropagation();
							await onDeleteEstimateGroup(row.estimateGroupId);
						}}
					/>
				</Space>
			),
			width: 80,
		},
	], [refetch, onDeleteEstimateGroup]);

	const formFields: FormPopoverProps<CreateEstimateFormData>['fields'] = useMemo(() => [
		{
			label: 'Estimate Type',
			name: 'type',
			type: 'select',
			required: true,
			inputProps: {
				'data-testid': 'estimate-type',
			},
			options: [{
				label: FixtureTypeLabels[FixtureTypes.TC_OUT],
				value: FixtureTypes.TC_OUT,
			},
			{
				label: FixtureTypeLabels[FixtureTypes.SPOT],
				value: FixtureTypes.SPOT,
			}],
		},
		{
			label: 'Vessel',
			name: 'vesselId',
			type: 'select',
			required: false,
			options: undefined,
			renderInput: () => (
				<VesselSelector
					includeVesselIndex
					includeMarketVessels
					showNoVessel
					variant="outlined"
				/>
			),
		},
		{
			label: 'Name',
			name: 'name',
			type: 'text',
			required: false,
		},
		{
			label: 'Currency',
			name: 'currency',
			type: 'select',
			required: true,
			inputProps: {
				showSearch: true,
			},
			options: Object.keys(Currencies).map((c) => ({
				label: c,
				value: c,
			})),
		},
	], []);

	const submitCreateEstimateForm = async (values: CreateEstimateFormData) => {
		try {
			const { estimateGroupId, id } = await createEstimate(values);

			await refetch();
			showSuccessNotification(
				'Estimate created',
				'The estimate was successfully created',
			);

			// this is for the post submit redirect
			return {
				estimateGroupId: estimateGroupId,
				firstEstimateId: id,
			};
		} catch (e) {
			showErrorNotification('Could not create estimate', e as Error);

			return null;
		}
	};

	return (
		<ListTableScreen<EstimateOverviewTableRow, CreateEstimateFormData>
			title="Estimates - Out"
			rootPageTitle="Estimates - Out"
			searchExtra={(
				<Checkbox checked={hideFixed} onChange={(e) => setHideFixed(e.target.checked)}>
					Hide fixed estimates
				</Checkbox>
			)}
			columns={columns}
			loading={isLoading}
			emptyTitle="No estimates found"
			emptyIcon={faInbox}
			error={error as Error}
			data={estimateRows}
			tableProps={{
				size: 'small',
				scroll: { x: 1300 },
				onChange: onTableChange,
				pagination: {
					pageSizeOptions: [50, 100, 200, 500],
					current: pagination?.current,
					pageSize: pagination?.pageSize,
					total: estimateGroups?.count ?? 0,
				},
			}}
			formFields={formFields}
			formProps={{
				initialValues: {
					type: FixtureTypes.SPOT,
					currency: Currencies.USD,
				},
			}}
			formButtonText="Create estimate"
			onFormSubmit={submitCreateEstimateForm}
			getRowHref={
				(row) => Links.Fixtures[FixtureTypes.SPOT].Details.get(
					row.estimateGroupId,
					row.firstEstimateId ?? row.id,
				)
			}
			redirectOnSubmit
			showEmptyArrow
			showSearch
			onSearch={setSearch}
			searchAttributes={[
				'name',
				'id',
				'readableId',
				'vesselName',
				'firstLoadPortName',
				'lastDischargePortName',
				'charterers',
			]}
		/>
	);
};

export default EstimatesListPage;
